import { Box, Tabs, Tab } from '@mui/material';
import React, { useState, useContext, useCallback, useEffect } from 'react';

import { TabPanel } from '../common';
import AuthContext from '../auth/context/AuthContext';
import { isEmpty } from 'lodash';
import CompletedSentReferrals from './tabs/CompletedSentReferrals';
import CompletedReceivedReferrals from './tabs/CompletedReceivedReferrals';
import DialogReferralNotes from './dialogs/DialogReferralNotes';

export default function CompletedReferrals({ history }) {
  const currentUser = useContext(AuthContext);

  const hasSentTab =
    !isEmpty(currentUser) &&
    currentUser.referral_permissions.some(item => item.key === 'SEND_REFERRAL' && item.value);

  const hasReceivedTab =
    !isEmpty(currentUser) &&
    currentUser.clinic_providers > 0 &&
    currentUser.referral_permissions.some(
      item => (item.key === 'ACCEPT_REFERRAL' || item.key === 'SCHEDULE_REFERRAL') && item.value
    );

  // Determine initial tab from URL or fallback to available tab
  const getInitialTab = () => {
    const params = new URLSearchParams(history.location.search);
    const tab = params.get('tab');

    if (tab === 'sent' && !hasSentTab) return hasReceivedTab ? 'received' : null;
    if (tab === 'received' && !hasReceivedTab) return hasSentTab ? 'sent' : null;
    if (!tab) return hasSentTab ? 'sent' : hasReceivedTab ? 'received' : null;

    return tab;
  };

  const [value, setValue] = useState(getInitialTab);
  const [referralUuid, setReferralUuid] = useState('');
  const [openNotes, setOpenNotes] = useState(false);

  const handleClose = () => setOpenNotes(false);

  const handleChange = useCallback(
    (event, newValue) => {
      setValue(newValue);
      const url = `${history.location.pathname}?tab=${newValue}`;
      history.replace(url);
    },
    [history]
  );

  const handleOnViewNotes = useCallback(rowId => {
    setReferralUuid(rowId);
    setOpenNotes(true);
  }, []);

  const handleOnReview = useCallback(
    rowId => {
      history.push(`/referrals/${rowId}/review`);
    },
    [history]
  );

  // If current tab is no longer allowed, switch to first available tab
  useEffect(() => {
    if ((value === 'sent' && !hasSentTab && hasReceivedTab) ||
      (value === 'received' && !hasReceivedTab && hasSentTab)) {
      const firstTab = hasSentTab ? 'sent' : hasReceivedTab ? 'received' : null;
      if (firstTab) handleChange(null, firstTab);
    }
  }, [value, hasSentTab, hasReceivedTab, handleChange]);

  if (!hasSentTab && !hasReceivedTab) return null;

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="completed referrals"
            centered
            textColor="secondary"
          >
            {hasSentTab && <Tab label="Completed Sent Referrals" value="sent" />}
            {hasReceivedTab && <Tab label="Completed Received Referrals" value="received" />}
          </Tabs>
        </Box>

        {hasSentTab && value === 'sent' && (
          <TabPanel value={value} index="sent">
            <CompletedSentReferrals
              uuid={currentUser.uuid}
              onViewLogs={handleOnViewNotes}
              onView={handleOnReview}
            />
          </TabPanel>
        )}

        {hasReceivedTab && value === 'received' && (
          <TabPanel value={value} index="received">
            <CompletedReceivedReferrals
              uuid={currentUser.uuid}
              onViewLogs={handleOnViewNotes}
              onView={handleOnReview}
            />
          </TabPanel>
        )}
      </Box>

      <DialogReferralNotes
        referralUuid={referralUuid}
        openNotes={openNotes}
        handleClose={handleClose}
      />
    </>
  );
}